import { useState, useEffect } from 'react';
import { Grid3X3, ChevronDown, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { appService, WorkspaceSubscriptionWithApp } from '../services/app.service';
import { useAppSubscription } from '../contexts/AppSubscriptionContext';

export const AppDropdown = () => {
  const { user, currentWorkspace } = useAuth();
  const { refreshTrigger } = useAppSubscription();
  const { t } = useTranslation('shared');
  const [apps, setApps] = useState<WorkspaceSubscriptionWithApp[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadApps = async () => {
      if (!user || !currentWorkspace) {
        setApps([]);
        return;
      }

      setLoading(true);
      try {
        const idToken = await user.getIdToken();
        const subscriptions = await appService.getWorkspaceSubscriptions(
          currentWorkspace.id,
          idToken
        );
        setApps(subscriptions);
      } catch (error) {
        console.error('Failed to load apps:', error);
        setApps([]);
      } finally {
        setLoading(false);
      }
    };

    loadApps();
  }, [user, currentWorkspace, refreshTrigger]);

  const handleAppClick = (slug: string) => {
    // Use hard navigation to load the external app
    window.location.href = `/${slug}/`;
  };

  const handleBrowseAllApps = () => {
    // Navigate to portal home
    window.location.href = '/';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center space-x-2">
          <Grid3X3 className="w-4 h-4" />
          <span>{t('appDropdown.apps')}</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {loading ? (
          <DropdownMenuItem disabled>{t('appDropdown.loading')}</DropdownMenuItem>
        ) : apps.length === 0 ? (
          <DropdownMenuItem disabled>{t('appDropdown.empty')}</DropdownMenuItem>
        ) : (
          apps.map((app) => (
            <DropdownMenuItem
              key={app.app_id}
              onClick={() => handleAppClick(app.app_slug)}
              className="flex items-center justify-between"
            >
              <span>{app.app_name}</span>
              {app.app_is_beta && (
                <span className="text-xs text-orange-600 ml-2">{t('appDropdown.betaTag')}</span>
              )}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleBrowseAllApps}>
          <Home className="w-4 h-4 mr-2" />
          {t('appDropdown.browseAll')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
